import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { upsertProductionHandoverPhysicalReport } from '../../../../api/productionHandoverApi';

const emptyForm = {
  sscc: '',
  reportType: 'EXTRA_HU',
  comment: '',
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  if (responseMessage) {
    return responseMessage;
  }

  return fallbackMessage;
};

const PhysicalDiscrepancyDialog = ({ open, handover, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(emptyForm);
    setError('');
  }, [open, handover?.id]);

  const normalizedSsccPreview = useMemo(() => {
    if (/^\d{18}$/.test(form.sscc)) {
      return `00${form.sscc}`;
    }

    if (/^00\d{18}$/.test(form.sscc)) {
      return form.sscc;
    }

    return '';
  }, [form.sscc]);

  const isSsccValid =
    /^\d{18}$/.test(form.sscc) || /^00\d{18}$/.test(form.sscc);

  const handleSsccChange = (event) => {
    const value = String(event.target.value ?? '')
      .replace(/\D/g, '')
      .slice(0, 20);

    setForm((previous) => ({
      ...previous,
      sscc: value,
    }));

    setError('');
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (!handover?.id) {
      setError('Nie wybrano zlecenia produkcyjnego.');
      return;
    }

    if (!isSsccValid) {
      setError(
        'Wprowadź 18-cyfrowy numer SSCC albo 20-cyfrowy numer HU rozpoczynający się od 00.',
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await upsertProductionHandoverPhysicalReport(handover.id, {
        sscc: form.sscc,
        reportType: form.reportType,
        comment: form.comment.trim() || undefined,
      });

      await onSaved?.(result);

      onClose();
    } catch (saveError) {
      console.error(saveError);

      setError(
        getErrorMessage(
          saveError,
          'Nie udało się zapisać fizycznej niezgodności.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="warning" />

          <Typography variant="h6">Zgłoś niezgodną paletę</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Zgłoszenie dotyczy rzeczywistej palety zauważonej przez użytkownika.
            System porówna numer SSCC z wynikiem automatycznej analizy.
          </Alert>

          <Stack spacing={0.5}>
            <Typography variant="body2">
              Zlecenie: <strong>{handover?.productionOrder || '-'}</strong>
            </Typography>

            <Typography variant="body2">
              Partia: <strong>{handover?.batch || '-'}</strong>
            </Typography>

            <Typography variant="body2">
              Linia: <strong>{handover?.productionLine || '-'}</strong>
            </Typography>
          </Stack>

          <TextField
            label="Numer etykiety palety / SSCC"
            value={form.sscc}
            onChange={handleSsccChange}
            fullWidth
            required
            autoFocus
            disabled={loading}
            error={Boolean(form.sscc) && !isSsccValid}
            helperText={
              normalizedSsccPreview
                ? `Numer zapisany w systemie: ${normalizedSsccPreview}`
                : 'Wprowadź 18 cyfr SSCC lub 20 cyfr numeru HU z prefiksem 00.'
            }
            inputProps={{
              inputMode: 'numeric',
              maxLength: 20,
            }}
          />

          <FormControl fullWidth required>
            <InputLabel>Rodzaj niezgodności</InputLabel>

            <Select
              label="Rodzaj niezgodności"
              value={form.reportType}
              disabled={loading}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  reportType: event.target.value,
                }))
              }
            >
              <MenuItem value="EXTRA_HU">Dodatkowa paleta fizyczna</MenuItem>

              <MenuItem value="MISSING_HU">Brakująca paleta fizyczna</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Komentarz"
            value={form.comment}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                comment: event.target.value,
              }))
            }
            fullWidth
            multiline
            minRows={3}
            disabled={loading}
            inputProps={{
              maxLength: 1000,
            }}
            helperText={`${form.comment.length}/1000`}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Anuluj
        </Button>

        <Button
          variant="contained"
          color="warning"
          startIcon={
            loading ? <CircularProgress size={18} /> : <WarningAmberIcon />
          }
          onClick={handleSubmit}
          disabled={loading || !isSsccValid}
        >
          Zapisz niezgodność
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhysicalDiscrepancyDialog;
